/**
 * database-admin-rpc-client
 *
 * The admin Databases page's RPCs on the "db" mailbox:
 * dbaccess.v1.ListDatabases / SaveDatabase / DeleteDatabase / TestDatabase /
 * UpdateDatabaseJob. ContinuumAdmin only — the server checks every call. A
 * password goes up once (Save or Test), sealed to the mesh's key so the request
 * carries none in the clear, and never comes back: the server stores it
 * encrypted and reports only has_password.
 *
 * Where a database runs is part of the row: no worker means the mesh opens it;
 * a worker means that worker's job-runner keeps a daemon job (a8 db-serve) that
 * holds the connection, and the mesh forwards every statement to it. The server
 * creates, moves and retires that job from what Save says
 * (FEATURE-20261001-database-connection-executed-by-a-worker-daemon).
 */

import GlobalClient from "../hermesclient/client";
import { ContentType, RpcFrameType } from "../hermesclient/proto/wsmessages";
import { nameMappingService } from "./NameMappingService";
import { asLogicalMailbox, MailboxAddress } from "../hermesclient/types";
import { sealP256 } from "./sealed";

export type Dialect = "postgres" | "trino" | "mysql" | "sqlserver";

export const DIALECTS: Dialect[] = ["postgres", "trino", "mysql", "sqlserver"];

export interface DatabaseConnection {
  uid: string;
  name: string;
  dialect: string;
  url: string;
  credentialRef: string;
  hasPassword: boolean;
  description: string;
  modelSource: string;
  tableCount: number;
  /** the daemon job that runs this database's queries; "" = the mesh runs them */
  jobUid: string;
  /** the worker that job runs on (job.workeruid), and its login@server label */
  workerUid: string;
  workerLabel: string;
  /** the account the job runs as on that worker */
  executionUser: string;
  /** a process of the job is registered right now */
  jobLive: boolean;
}

export interface TestResult {
  ok: boolean;
  error: string;
  serverVersion: string;
  elapsedMillis: number;
}

export interface SaveInput {
  uid?: string;
  name: string;
  dialect: string;
  url: string;
  description?: string;
  password?: string;
  clearPassword?: boolean;
  credentialRef?: string;
  testFirst?: boolean;
  /**
   * Where the database runs. undefined = leave as stored; "" = the mesh (the job,
   * if any, is retired); a worker uid = that worker's job-runner runs a job for it.
   */
  workerUid?: string;
  /** the account the job runs as; "" = the worker's own login */
  executionUser?: string;
}

export interface SaveResult {
  success: boolean;
  error: string;
  database?: DatabaseConnection;
  test?: TestResult;
  /** what happened to the database's job, when it has one ("job restarted on ...") */
  jobMessage: string;
}

export interface UpdateJobResult {
  success: boolean;
  error: string;
  message: string;
  database?: DatabaseConnection;
}

// Wire shapes: protojson defaults (lowerCamel names); unpopulated fields are omitted.
interface RawDatabase {
  uid?: string;
  name?: string;
  dialect?: string;
  url?: string;
  credentialRef?: string;
  hasPassword?: boolean;
  description?: string;
  modelSource?: string;
  tableCount?: number;
  jobUid?: string;
  workerUid?: string;
  workerLabel?: string;
  executionUser?: string;
  jobLive?: boolean;
}

interface RawTest {
  ok?: boolean;
  error?: string;
  serverVersion?: string;
  elapsedMillis?: number | string;
}

export function toDatabase(raw: RawDatabase | undefined): DatabaseConnection | undefined {
  if (!raw) return undefined;
  return {
    uid: raw.uid ?? "",
    name: raw.name ?? "",
    dialect: raw.dialect ?? "",
    url: raw.url ?? "",
    credentialRef: raw.credentialRef ?? "",
    hasPassword: raw.hasPassword ?? false,
    description: raw.description ?? "",
    modelSource: raw.modelSource ?? "",
    tableCount: raw.tableCount ?? 0,
    jobUid: raw.jobUid ?? "",
    workerUid: raw.workerUid ?? "",
    workerLabel: raw.workerLabel ?? "",
    executionUser: raw.executionUser ?? "",
    jobLive: raw.jobLive ?? false,
  };
}

export function toTest(raw: RawTest | undefined): TestResult | undefined {
  if (!raw) return undefined;
  return {
    ok: raw.ok ?? false,
    error: raw.error ?? "",
    serverVersion: raw.serverVersion ?? "",
    // int64 arrives as a string in protojson.
    elapsedMillis: Number(raw.elapsedMillis ?? 0),
  };
}

let targetMailbox: MailboxAddress | null = null;

async function rpcCall<TResp>(endPoint: string, request: Record<string, unknown>): Promise<TResp> {
  if (!targetMailbox) {
    targetMailbox = await nameMappingService.resolve(asLogicalMailbox("db"));
  }
  const rpcResponse = await GlobalClient.get().rawRpcCall({
    to: targetMailbox,
    endPoint,
    body: new TextEncoder().encode(JSON.stringify(request)),
    contentType: ContentType.Json,
  });
  if (rpcResponse.header?.rpcHeader?.frameType === RpcFrameType.ErrorResponse) {
    const errorInfo = rpcResponse.header?.rpcHeader?.errorInfo;
    throw new Error(
      errorInfo?.message || errorInfo?.errorNode?.frame?.message || `RPC call to ${endPoint} failed`,
    );
  }
  if (!rpcResponse.data || rpcResponse.data.length === 0) {
    return {} as TResp;
  }
  return JSON.parse(new TextDecoder().decode(rpcResponse.data)) as TResp;
}

/** What a database's password is sealed for: the row and the url its request names. */
export function passwordContext(uid: string, url: string): string {
  return `database-password:${uid}:${url}`;
}

/**
 * A password as it crosses the mesh: sealed to the mesh's key for the row and url of the
 * request, never in the clear. No password stays no password.
 */
async function sealedPassword(uid: string, url: string, password: string): Promise<string> {
  if (password === "") return "";
  const resp = await rpcCall<{ sealingKey?: string }>("dbaccess.v1.ListDatabases", {});
  if (!resp.sealingKey) {
    throw new Error(
      "The mesh published no key to seal the password to: it is older than this page, or its config has no key to store passwords with.",
    );
  }
  return sealP256(resp.sealingKey, passwordContext(uid, url), password);
}

export async function listDatabases(): Promise<DatabaseConnection[]> {
  const resp = await rpcCall<{ databases?: RawDatabase[] }>("dbaccess.v1.ListDatabases", {});
  return (resp.databases ?? []).map((d) => toDatabase(d) as DatabaseConnection);
}

export async function saveDatabase(input: SaveInput): Promise<SaveResult> {
  const request: Record<string, unknown> = {
    uid: input.uid ?? "",
    name: input.name,
    dialect: input.dialect,
    url: input.url,
    description: input.description ?? "",
    password: await sealedPassword(input.uid ?? "", input.url, input.password ?? ""),
    clearPassword: input.clearPassword ?? false,
    credentialRef: input.credentialRef ?? "",
    testFirst: input.testFirst ?? false,
    executionUser: input.executionUser ?? "",
  };
  // worker_uid is an optional proto field: left out of the JSON it means "as stored";
  // present and empty it means the mesh. So it goes up only when the caller chose.
  if (input.workerUid !== undefined) request["workerUid"] = input.workerUid;
  const resp = await rpcCall<{
    success?: boolean;
    error?: string;
    database?: RawDatabase;
    test?: RawTest;
    jobMessage?: string;
  }>("dbaccess.v1.SaveDatabase", request);
  return {
    success: resp.success ?? false,
    error: resp.error ?? "",
    database: toDatabase(resp.database),
    test: toTest(resp.test),
    jobMessage: resp.jobMessage ?? "",
  };
}

/** Brings a database's job in line with its row and restarts it (a changed url or password reaches the process). */
export async function updateDatabaseJob(uid: string): Promise<UpdateJobResult> {
  const resp = await rpcCall<{ success?: boolean; error?: string; message?: string; database?: RawDatabase }>(
    "dbaccess.v1.UpdateDatabaseJob",
    { uid },
  );
  return {
    success: resp.success ?? false,
    error: resp.error ?? "",
    message: resp.message ?? "",
    database: toDatabase(resp.database),
  };
}

export async function deleteDatabase(uid: string): Promise<{ success: boolean; error: string }> {
  const resp = await rpcCall<{ success?: boolean; error?: string }>("dbaccess.v1.DeleteDatabase", { uid });
  return { success: resp.success ?? false, error: resp.error ?? "" };
}

/**
 * Tests a saved row (uid), an edit of one (uid plus overrides), or an unsaved connection.
 * workerUid says where the test connects from: undefined = where the stored row runs
 * (the mesh for an unsaved one); "" = the mesh; a worker uid = that worker's job-runner.
 */
export async function testDatabase(input: {
  uid?: string;
  dialect?: string;
  url?: string;
  password?: string;
  credentialRef?: string;
  workerUid?: string;
}): Promise<TestResult> {
  const request: Record<string, unknown> = {
    uid: input.uid ?? "",
    dialect: input.dialect ?? "",
    url: input.url ?? "",
    password: await sealedPassword(input.uid ?? "", input.url ?? "", input.password ?? ""),
    credentialRef: input.credentialRef ?? "",
  };
  if (input.workerUid !== undefined) request["workerUid"] = input.workerUid;
  const resp = await rpcCall<RawTest>("dbaccess.v1.TestDatabase", request);
  return toTest(resp) as TestResult;
}
